import React, { useState } from 'react';
import { ActivityIcon, KeyRoundIcon, PauseIcon, PlayIcon, PlugIcon, RefreshCwIcon, WebhookIcon } from 'lucide-react';
import { PageHeader } from '../../components/ui/PageHeader';
import { Panel } from '../../components/ui/Panel';
import { Badge } from '../../components/ui/Badge';
import { Button } from '../../components/ui/Button';
import { Tabs } from '../../components/ui/Tabs';
import { Modal } from '../../components/ui/Modal';
import { Field, Textarea } from '../../components/ui/Field';
import { PermissionGate } from '../../components/admin/PermissionGate';
import { useGovernance } from '../../contexts/GovernanceContext';
import type { HealthState } from '../../types/governance';
const TABS = ['Integrations', 'Webhooks', 'Background jobs', 'Emergency controls'] as const;
const HEALTH: Record<HealthState, {
  label: string;
  tone: 'ok' | 'warn' | 'danger' | 'neutral';
}> = {
  healthy: {
    label: 'Healthy',
    tone: 'ok'
  },
  degraded: {
    label: 'Degraded',
    tone: 'warn'
  },
  down: {
    label: 'Down',
    tone: 'danger'
  },
  unknown: {
    label: 'Unknown',
    tone: 'neutral'
  }
};
function stamp(value: string | null): string {
  if (!value) return 'Never';
  return new Date(value).toLocaleString(undefined, {
    day: 'numeric',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit'
  });
}
export function SystemHealth() {
  const [tab, setTab] = useState<(typeof TABS)[number]>('Integrations');
  return <div className="space-y-6" data-id="element-7835" data-mp="16uwpn4i8scke">
      <PageHeader eyebrow="Administration" title="System health" description="Integrations, webhook processing, background work and the emergency controls. Nothing operationally important is allowed to fail silently." actions={<Badge tone="ok" plain data-id="element-7837" data-mp="t3i68wkfa9ct">
            Production environment
          </Badge>} data-id="element-7836" data-mp="16x9437osk4sp" />

      <Tabs tabs={TABS} value={tab} onChange={setTab} label="System health sections" data-id="element-7838" data-mp="1qkymwper2ups" />

      {tab === 'Integrations' && <IntegrationsTab data-id="element-7839" data-mp="1lqm3gfd9xje1" />}
      {tab === 'Webhooks' && <WebhooksTab data-id="element-7840" data-mp="24onw4mjcy7om" />}
      {tab === 'Background jobs' && <JobsTab data-id="element-7841" data-mp="24r0mno36lqda" />}
      {tab === 'Emergency controls' && <EmergencyTab data-id="element-7842" data-mp="29oaaizax9tz0" />}
    </div>;
}
function IntegrationsTab() {
  const {
    integrations
  } = useGovernance();
  return <Panel title="External integrations" description="Credentials are never displayed. What is shown is whether they exist, when they were rotated, and whether the provider is answering." bodyClassName="p-0" data-id="element-7843" data-mp="1vw462ah4tbfe">
      <ul className="divide-y divide-line" data-id="element-7844" data-mp="1m0so7jaffo3f">
        {integrations.map(integration => {
        const health = HEALTH[integration.health];
        return <li key={integration.id} className="px-5 py-4 sm:px-6" data-id="element-7845" data-mp="1czce7cnixnzq">
              <div className="flex flex-wrap items-start justify-between gap-x-4 gap-y-2" data-id="element-7846" data-mp="1gyl76faqphu8">
                <div className="min-w-0" data-id="element-7847" data-mp="37b9bhm8ndo2p">
                  <div className="flex flex-wrap items-center gap-2" data-id="element-7848" data-mp="37calrohua6jd">
                    <PlugIcon aria-hidden className="h-5 w-5 shrink-0 text-ink-subtle" data-id="element-7849" data-mp="3bdnqfv9743op" />
                    <p className="text-body-lg font-semibold text-ink" data-id="element-7850" data-mp="zpiv6ak2ked2">{integration.provider}</p>
                  </div>
                  <p className="mt-1 max-w-[74ch] text-body-sm text-ink-muted" data-id="element-7851" data-mp="vpqmlxuoe87p">
                    {integration.purpose}
                  </p>
                </div>
                <div className="flex shrink-0 flex-wrap items-center gap-2" data-id="element-7852" data-mp="14pdoslv8bi1t">
                  <Badge plain data-id="element-7853" data-mp="14nanh5omm2hs">{integration.environment}</Badge>
                  <Badge tone={health.tone} data-id="element-7854" data-mp="1jcgmasjjxqp5">{health.label}</Badge>
                  {!integration.configured && <Badge tone="warn" data-id="element-7855" data-mp="1ee5dml4ztm8i">Not configured</Badge>}
                </div>
              </div>
              <dl className="mt-3 grid gap-3 sm:grid-cols-4" data-id="element-7856" data-mp="1sdeuzo4xqme2">
                <div data-id="element-7857" data-mp="1nf3it6r63zso">
                  <dt className="text-body-sm text-ink-muted" data-id="element-7858" data-mp="1ngofb1x1lst6">Last success</dt>
                  <dd className="text-body text-ink" data-id="element-7859" data-mp="3u5jhul7kuthx">{stamp(integration.lastSuccessAt)}</dd>
                </div>
                <div data-id="element-7860" data-mp="3p7rmfh7q4p8z">
                  <dt className="text-body-sm text-ink-muted" data-id="element-7861" data-mp="3p8j922jcfcn4">Last failure</dt>
                  <dd className="text-body text-ink" data-id="element-7862" data-mp="3rklh4zdmqz9i">{stamp(integration.lastFailureAt)}</dd>
                </div>
                <div data-id="element-7863" data-mp="3vlx7560f1mi8">
                  <dt className="text-body-sm text-ink-muted" data-id="element-7864" data-mp="3vpbd0o8agduf">Webhook signature</dt>
                  <dd className="text-body text-ink" data-id="element-7865" data-mp="4hgnwfbdrxqr">
                    {integration.webhookVerified ? 'Verified' : 'Not verified'}
                  </dd>
                </div>
                <div data-id="element-7866" data-mp="37yp30req4jhs">
                  <dt className="text-body-sm text-ink-muted" data-id="element-7867" data-mp="37z8kk70gp5va">Credentials rotated</dt>
                  <dd className="text-body text-ink" data-id="element-7868" data-mp="3cxilky8vb0ot">{stamp(integration.credentialRotatedAt)}</dd>
                </div>
              </dl>
              {!integration.configured && <p className="mt-2 flex items-start gap-2 text-body-sm text-warn-700" data-id="element-7869" data-mp="3mmtt4gg3vxal">
                  <KeyRoundIcon aria-hidden className="mt-0.5 h-4 w-4 shrink-0" data-id="element-7870" data-mp="2p7o4u5lfgrza" />
                  No credentials are held for this provider, so every capability that depends on it
                  stays unavailable rather than failing at the point of use.
                </p>}
            </li>;
      })}
      </ul>
    </Panel>;
}
function WebhooksTab() {
  const {
    webhooks
  } = useGovernance();
  return <Panel title="Webhook processing" description="Received, verified, processed, retried or dead-lettered. A rejected payload is held for inspection, never discarded." bodyClassName="p-0" data-id="element-7871" data-mp="2cmzbknmhcstf">
      <ul className="divide-y divide-line" data-id="element-7872" data-mp="23p6x1lol1p73">
        {webhooks.map(event => <li key={event.id} className="px-5 py-4 sm:px-6" data-id="element-7873" data-mp="1yvksyhwhitmw">
            <div className="flex flex-wrap items-start justify-between gap-x-4 gap-y-2" data-id="element-7874" data-mp="1txj9vwz26ubf">
              <div className="min-w-0" data-id="element-7875" data-mp="1oz7znl4uqhso">
                <div className="flex flex-wrap items-center gap-2" data-id="element-7876" data-mp="1p0aolnim6a5y">
                  <WebhookIcon aria-hidden className="h-5 w-5 shrink-0 text-ink-subtle" data-id="element-7877" data-mp="1k0ejtm23eepc" />
                  <p className="text-body font-semibold text-ink" data-id="element-7878" data-mp="1fyjocy6uevjh">{event.provider}</p>
                  <span className="cz-num text-body-sm text-ink-muted" data-id="element-7879" data-mp="15d64qa5nxp83">{event.event}</span>
                </div>
                <p className="mt-1 max-w-[74ch] text-body-sm text-ink" data-id="element-7880" data-mp="1ac10ecoqb89m">{event.detail}</p>
                <p className="cz-num mt-0.5 text-caption text-ink-subtle" data-id="element-7881" data-mp="wchsnkztxyxu">
                  {stamp(event.receivedAt)} · {event.attempts} attempt
                  {event.attempts === 1 ? '' : 's'}
                </p>
              </div>
              <Badge tone={event.state === 'processed' || event.state === 'verified' ? 'ok' : event.state === 'retried' ? 'warn' : 'danger'} data-id="element-7882" data-mp="1p288byh418w2">
                {event.state.replace(/_/g, ' ')}
              </Badge>
            </div>
          </li>)}
      </ul>
    </Panel>;
}
function JobsTab() {
  const {
    jobs
  } = useGovernance();
  return <Panel title="Background work" description="Every job has an owner. A failure that nobody owns is a failure nobody fixes." bodyClassName="p-0" data-id="element-7883" data-mp="2crt11932fkij">
      <ul className="divide-y divide-line" data-id="element-7884" data-mp="2i480c4ccgoqi">
        {jobs.map(job => <li key={job.id} className="px-5 py-4 sm:px-6" data-id="element-7885" data-mp="2i257lcjt4hi3">
            <div className="flex flex-wrap items-start justify-between gap-x-4 gap-y-2" data-id="element-7886" data-mp="2vu1oekg0z4z6">
              <div className="min-w-0" data-id="element-7887" data-mp="2qxb8bx4809dr">
                <div className="flex flex-wrap items-center gap-2" data-id="element-7888" data-mp="2qycsqs74m885">
                  <ActivityIcon aria-hidden className="h-5 w-5 shrink-0 text-ink-subtle" data-id="element-7889" data-mp="237gyzpj412y9" />
                  <p className="text-body font-semibold text-ink" data-id="element-7890" data-mp="1z6f5zqfsdn4q">{job.name}</p>
                </div>
                <p className="mt-0.5 text-body-sm text-ink-muted" data-id="element-7891" data-mp="2d5ob9v2gewtx">
                  {job.queue} queue · owned by {job.owner} · started {stamp(job.startedAt)}
                </p>
                {job.error && <p className="mt-1.5 max-w-[74ch] text-body-sm text-danger-700" data-id="element-7892" data-mp="3oigzivw1d358">{job.error}</p>}
                {job.nextRetryAt && <p className="mt-0.5 text-caption text-ink-subtle" data-id="element-7893" data-mp="1hwb8soedygs4">
                    Next attempt {stamp(job.nextRetryAt)}
                  </p>}
              </div>
              <div className="flex shrink-0 flex-wrap items-center gap-2" data-id="element-7894" data-mp="1qwg2jvqnpgnt">
                <Badge tone={job.state === 'succeeded' ? 'ok' : job.state === 'failed' ? 'danger' : job.state === 'running' ? 'brand' : 'warn'} data-id="element-7895" data-mp="1qud5wsv00pa4">
                  {job.state}
                </Badge>
                {(job.state === 'failed' || job.state === 'retrying') && <PermissionGate permission="config.edit" label="Retrying a background job" data-id="element-7896" data-mp="2ejy0ya4ffczr">
                    <Button variant="secondary" size="sm" icon={<RefreshCwIcon className="h-4 w-4" data-id="element-7898" data-mp="ka3wesbubk57" />} data-id="element-7897" data-mp="faraqawpgj47">
                      Retry now
                    </Button>
                  </PermissionGate>}
              </div>
            </div>
          </li>)}
      </ul>
    </Panel>;
}
function EmergencyTab() {
  const {
    switches,
    setSwitch,
    allows
  } = useGovernance();
  const [pending, setPending] = useState<{
    id: string;
    to: 'enabled' | 'paused';
  } | null>(null);
  const [reason, setReason] = useState('');
  const decision = allows('security.emergency');
  const target = switches.find(item => item.id === pending?.id) ?? null;
  return <div className="space-y-5" data-id="element-7899" data-mp="lon9cn7h3td1">
      <Panel title="What these controls do" data-id="element-7900" data-mp="lnlndnox5mgj">
        <p className="max-w-[76ch] text-body-sm text-ink" data-id="element-7901" data-mp="hnl860tzx78f">
          Pausing a capability refuses new activity. It never deletes data, and records already in
          flight continue through their own recovery and reconciliation. Every change requires a
          reason, is written to the audit log, and raises an alert.
        </p>
        {!decision.allowed && <p className="mt-3 rounded-md border border-line bg-canvas px-3 py-2.5 text-body-sm text-ink" data-id="element-7902" data-mp="2cb7f3pzxeojr">
            {decision.message}
          </p>}
      </Panel>

      <Panel title="Safety switches" bodyClassName="p-0" data-id="element-7903" data-mp="2m1it65dbqe4d">
        <ul className="divide-y divide-line" data-id="element-7904" data-mp="2haail609cmpm">
          {switches.map(item => <li key={item.id} className="px-5 py-4 sm:px-6" data-id="element-7905" data-mp="2h87krzsv5pac">
              <div className="flex flex-wrap items-start justify-between gap-x-4 gap-y-2" data-id="element-7906" data-mp="2vxde5750slab">
                <div className="min-w-0" data-id="element-7907" data-mp="2qz2691yackuq">
                  <p className="text-body-lg font-semibold text-ink" data-id="element-7908" data-mp="2r26jspxn4sy2">{item.label}</p>
                  <p className="mt-1 max-w-[74ch] text-body-sm text-ink" data-id="element-7909" data-mp="34xil5g6axhny">{item.effect}</p>
                  <p className="mt-1 text-caption text-ink-subtle" data-id="element-7910" data-mp="301bchfmits2b">
                    {item.lastChangedBy ? `Last changed by ${item.lastChangedBy} · ${stamp(item.lastChangedAt)}` : 'Never changed'}
                  </p>
                </div>
                <div className="flex shrink-0 flex-wrap items-center gap-2" data-id="element-7911" data-mp="3hady8c2zu9wo">
                  <Badge tone={item.state === 'enabled' ? 'ok' : item.state === 'paused' ? 'warn' : 'neutral'} data-id="element-7912" data-mp="3m7vxtbv4fcs0">
                    {item.state === 'unavailable' ? 'Unavailable' : item.state}
                  </Badge>
                  {item.state === 'unavailable' ? <span className="text-caption text-ink-subtle" data-id="element-7913" data-mp="33g393tn34dq3">No provider configured</span> : <Button variant={item.state === 'enabled' ? 'danger' : 'secondary'} size="sm" icon={item.state === 'enabled' ? <PauseIcon className="h-4 w-4" data-id="element-7915" data-mp="rrlptqxijens" /> : <PlayIcon className="h-4 w-4" data-id="element-7916" data-mp="20qwmte4eicmt" />} disabled={!decision.allowed} onClick={() => setPending({
                id: item.id,
                to: item.state === 'enabled' ? 'paused' : 'enabled'
              })} data-id="element-7914" data-mp="2tjqdgq7zbpsp">
                      {item.state === 'enabled' ? 'Pause' : 'Resume'}
                    </Button>}
                </div>
              </div>
            </li>)}
        </ul>
      </Panel>

      <Modal open={pending !== null} onClose={() => setPending(null)} title={target ? `${pending?.to === 'paused' ? 'Pause' : 'Resume'} ${target.label}` : 'Change capability'} description="This takes effect immediately across every surface and raises an alert." footer={<>
            <Button variant="secondary" onClick={() => setPending(null)} data-id="element-7918" data-mp="35ps4smh2az26">
              Cancel
            </Button>
            <Button variant={pending?.to === 'paused' ? 'danger' : 'primary'} disabled={!reason.trim()} onClick={() => {
        if (pending) setSwitch(pending.id, pending.to, reason.trim());
        setPending(null);
        setReason('');
      }} data-id="element-7919" data-mp="3aod5so451mro">
              {pending?.to === 'paused' ? 'Pause capability' : 'Resume capability'}
            </Button>
          </>} data-id="element-7917" data-mp="2am6q056jcq26">
        {target && <div className="space-y-4" data-id="element-7920" data-mp="29cmaiiyrmfk">
            <p className="text-body-sm text-ink" data-id="element-7921" data-mp="2ae18y33ot2k">{target.effect}</p>
            <Field label="Reason" htmlFor="switch-reason" data-id="element-7922" data-mp="76l9wymvsio7">
              <Textarea id="switch-reason" rows={3} value={reason} onChange={event => setReason(event.target.value)} placeholder="Recorded against your name on the audit log" data-id="element-7923" data-mp="b983p5344do1" />
            </Field>
          </div>}
      </Modal>
    </div>;
}